import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  Popper,
  Paper,
  MenuList,
  MenuItem,
  ClickAwayListener,
} from "@mui/material";

const generateTimes = () => {
  const times = [];
  let start = 0;
  const end = 24 * 60;
  while (start < end) {
    const hours = Math.floor(start / 60);
    const minutes = start % 60;
    const value = `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    const ampm = hours < 12 ? "AM" : "PM";
    const label = `${displayHours}:${displayMinutes} ${ampm}`;
    times.push({ label, value, minutes: start });
    start += 15;
  }
  return times;
};

// Convert 24hr -> 12hr
const formatTo12Hour = (time24) => {
  if (!time24) return "";
  const [h, m] = time24.split(":");
  let hours = parseInt(h, 10);
  const minutes = parseInt(m, 10);
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};

export function TimePickerASAP({
  value,
  onChange,
  disableBeforeMinutes,
  placeholder = "Enter time (e.g. 2:23 AM)",
}) {
  const times = generateTimes();
  const [inputValue, setInputValue] = useState(formatTo12Hour(value));
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    setInputValue(formatTo12Hour(value));
    setError(false); // Clear error when value is set externally
  }, [value]);

  const validateTime = (timeString) => {
    if (!timeString.trim()) return { isValid: true };
    
    const match = timeString.match(/^(\d{1,2}):(\d{2})\s?([AaPp][Mm])$/);
    if (!match) return { isValid: false };
    
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    
    if (hours < 1 || hours > 12) return { isValid: false };
    if (minutes < 0 || minutes > 59) return { isValid: false };
    
    return { isValid: true, hours, minutes, ampm: match[3].toLowerCase() };
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    setOpen(true);

    // If input is empty, call onChange with empty value
    if (val.trim() === "") {
      onChange("");
      setError(false);
      return;
    }

    const validation = validateTime(val);
    
    if (validation.isValid && validation.hours !== undefined) {
      setError(false);
      let hours = validation.hours;
      const minutes = validation.minutes;
      const ampm = validation.ampm;

      // Convert to 24-hour format
      if (ampm === "pm" && hours < 12) hours += 12;
      if (ampm === "am" && hours === 12) hours = 0;

      const formatted = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;

      onChange(formatted);
    } else {
      // Invalid time format or values
      setError(true);
    }
  };

  const handleSelect = (time) => {
    setInputValue(time.label);
    onChange(time.value);
    setOpen(false);
    setError(false);
  };

  // Handle when user clears the field manually
  const handleBlur = () => {
    if (inputValue.trim() === "") {
      onChange("");
      setError(false);
    } else if (error) {
      // If there's an error on blur, revert to the last valid value
      setInputValue(formatTo12Hour(value));
      setError(false);
    }
  };

  const handleFocus = () => {
    setOpen(true);
  };

  const handleClick = () => {
    setOpen(true);
  };

  const handleClickAway = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    
    // Ensure empty state is captured on click away
    if (inputValue.trim() === "") {
      onChange("");
      setError(false);
    } else if (error) {
      // Revert to last valid value
      setInputValue(formatTo12Hour(value));
      setError(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && !e.shiftKey) {
      setOpen(false);
    }
    
    // Handle clear with Backspace/Delete
    if ((e.key === 'Backspace' || e.key === 'Delete') && inputValue === "") {
      onChange("");
      setError(false);
    }
  };

  return (
    <div>
      <TextField
        inputRef={anchorRef}
        size="small"
        value={inputValue}
        onFocus={handleFocus}
        onClick={handleClick}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        error={error}
        helperText={error ? "Please enter a valid time (e.g. 2:30 PM)" : ""}
        sx={{ minWidth: 140 }}
      />

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              boundary: 'viewport',
            },
          },
        ]}
      >
        <Paper
          elevation={3}
          sx={{
            maxHeight: 250,
            overflowY: "auto",
            width: anchorRef.current ? anchorRef.current.clientWidth : 140,
            minWidth: 140,
          }}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <MenuList dense disablePadding>
              {times.map((time) => (
                <MenuItem
                  key={time.value}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(time)}
                  disabled={
                    disableBeforeMinutes !== undefined &&
                    time.minutes <= disableBeforeMinutes
                  }
                  sx={{ py: 0.5 }}
                >
                  {time.label}
                </MenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </div>
  );
}